import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Input, Segmented, Select, Spin } from 'antd';
import { DeleteOutlined, MailTwoTone, ReloadOutlined } from '@ant-design/icons';
import dynamicFormsAdminService from '../../services/dynamicFormsAdminService';
import useElementWidth from './useElementWidth';
import { formatFieldName } from './fieldTypes';

const { TextArea } = Input;

const KINDS = [
  {
    key: 'submission',
    label: 'To the applicant',
    title: 'Confirmation to the applicant',
    about: 'Sent to the applicant as soon as they send the form.',
    toLabel: 'If the applicant left no email, send to',
    toPlaceholder: 'fallback@festivals.mt',
  },
  {
    key: 'external',
    label: 'To reviewers',
    title: 'Review request',
    about: 'Sent to reviewers outside the team, with the application attached as a PDF.',
    toLabel: 'Send to',
    toPlaceholder: 'reviewer@example.com',
  },
  {
    key: 'internal',
    label: 'To your team',
    title: 'Internal notice',
    about: 'Sent to your team for their records, with the application attached as a PDF.',
    toLabel: 'Send to',
    toPlaceholder: 'team@festivals.mt',
  },
];

const PARTS = ['subject', 'body', 'footer', 'to', 'cc'];
const HTML_PARTS = ['body', 'footer'];

// Stored bodies are HTML with <br> for line breaks; edit them as plain lines.
const toLines = (html) => (html || '').replace(/<br\s*\/?>/gi, '\n');
const toHtml = (lines) => (lines ? lines.replace(/\n/g, '<br>') : null);

const valuesFrom = (config) => {
  const out = { applicant_email_field: config?.applicant_email_field || null };
  KINDS.forEach(({ key }) =>
    PARTS.forEach((part) => {
      const name = `${key}_${part}`;
      out[name] = HTML_PARTS.includes(part) ? toLines(config?.[name]) : config?.[name] || '';
    })
  );
  return out;
};

const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
const isEmail = (v) => !v || EMAIL_RE.test(v.trim());
// copies may list several addresses separated by commas, as the backend splits them
const isEmailList = (v) => !v || v.split(',').map((s) => s.trim()).filter(Boolean).every((s) => EMAIL_RE.test(s));

// What the backend sends when a text is left empty (see FM-BackEnd forms/emails.ts)
const SEND_DEFAULTS = {
  submission: { subject: 'Submission Confirmation', body: '<p>Thank you for your submission.</p>', footer: 'Kind regards,\nFestivals Portal Team' },
  external: { subject: 'New Application for Review', body: '<p>A new application requires your review.</p>', footer: 'Kind regards,\nFestivals Portal Team' },
  internal: { subject: 'New Form Submission', body: '<p>A new form submission has been received.</p>', footer: 'Kind regards,\nFestivals Portal System' },
};

/**
 * The three emails a submission sends, with their recipients. Same endpoint and saved shape as
 * before: bodies and footers are stored as HTML with line breaks as <br>.
 */
const EmailsEditor = ({ formId, fields }) => {
  const { message, modal } = App.useApp();
  const rootRef = useRef(null);
  const width = useElementWidth(rootRef);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [values, setValues] = useState(valuesFrom(null));
  const [kind, setKind] = useState('submission');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const fieldOptions = useMemo(
    () => [...fields].sort((a, b) => a.field_order - b.field_order).map((f) => ({ value: f.field_name, label: f.display_label || formatFieldName(f.field_name) })),
    [fields]
  );

  const load = async () => {
    try {
      setLoading(true);
      const response = await dynamicFormsAdminService.getEmailConfig(formId);
      setConfig(response);
      setValues(valuesFrom(response));
    } catch {
      // no configuration yet: the backend fills in its default texts on first save
      setConfig(null);
      setValues(valuesFrom(null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  // pick the question that looks like an email address when none is set, as before
  const emailField = values.applicant_email_field
    || fields.find((f) => f.field_name.toLowerCase().includes('email'))?.field_name
    || fields[0]?.field_name
    || null;

  const saved = valuesFrom(config);
  const dirty = Object.keys(values).some((k) => (values[k] || '') !== (saved[k] || '')) || (!config && !!emailField);

  const set = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const save = async () => {
    const found = {};
    KINDS.forEach(({ key }) => {
      if (!isEmail(values[`${key}_to`])) found[`${key}_to`] = 'Enter one email address.';
      if (!isEmailList(values[`${key}_cc`])) found[`${key}_cc`] = 'Enter email addresses separated by commas.';
    });
    if (!emailField) found.applicant_email_field = 'Choose the question that asks for the applicant’s email.';
    setErrors(found);
    if (Object.keys(found).length) {
      const firstKind = KINDS.find(({ key }) => Object.keys(found).some((n) => n.startsWith(`${key}_`)));
      if (firstKind) setKind(firstKind.key);
      return;
    }

    const payload = { applicant_email_field: emailField };
    KINDS.forEach(({ key }) => PARTS.forEach((part) => {
      const name = `${key}_${part}`;
      payload[name] = HTML_PARTS.includes(part) ? toHtml(values[name]) : values[name] || null;
    }));

    try {
      setSaving(true);
      await dynamicFormsAdminService.updateEmailConfig(formId, payload);
      message.success('Emails saved');
      await load();
    } catch {
      message.error('Could not save the emails');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    modal.confirm({
      title: 'Remove the email settings?',
      content: 'Texts and recipients for all three emails are removed. The next save starts again from the standard texts.',
      okText: 'Remove settings',
      okButtonProps: { danger: true },
      rootClassName: 'do-scope',
      onOk: async () => {
        try {
          await dynamicFormsAdminService.deleteEmailConfig(formId);
          message.success('Email settings removed');
          await load();
        } catch {
          message.error('Could not remove the email settings');
        }
      },
    });
  };

  if (loading && !config) {
    return <div className="dfw-empty" ref={rootRef}><Spin /></div>;
  }

  const current = KINDS.find((k) => k.key === kind);
  const v = (part) => values[`${kind}_${part}`] || '';
  const emailLabel = fieldOptions.find((o) => o.value === emailField)?.label;
  const wide = width >= 980;

  // the same frame the backend puts around the message
  const defaults = SEND_DEFAULTS[kind];
  const previewDoc = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;background:#fff;color:#222}</style></head><body>
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
    ${toHtml(v('body')) || defaults.body}
    <br><br>
    <p style="white-space: pre-line;">${toHtml(v('footer')) || defaults.footer.replace(/\n/g, '<br>')}</p>
    </div></body></html>`;

  return (
    <div className="dfw-emails" ref={rootRef}>
      <div className="dfw-builder__bar">
        <p className="dfw-builder__lede">
          {config ? 'Each response sends these three emails.' : 'No email settings yet. Saving creates them, starting from the standard texts.'}
        </p>
        <span className="dfw-grow" />
        <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={load} loading={loading}>Refresh</Button>
        {config && <Button danger type="text" icon={<DeleteOutlined aria-hidden="true" />} onClick={remove}>Remove settings</Button>}
      </div>

      <div className="dfw-field dfw-field--narrow">
        <label className="dfw-label" htmlFor="dfw-e-field">Question with the applicant’s email</label>
        <Select
          id="dfw-e-field"
          value={emailField}
          onChange={(val) => set('applicant_email_field', val)}
          options={fieldOptions}
          placeholder="Choose a question"
          status={errors.applicant_email_field ? 'error' : undefined}
        />
        {errors.applicant_email_field && <p className="dfw-error" role="alert">{errors.applicant_email_field}</p>}
      </div>

      <Segmented
        className="dfw-emails__kinds"
        value={kind}
        onChange={setKind}
        options={KINDS.map((k) => ({ value: k.key, label: k.label }))}
        aria-label="Email"
      />

      <div className={`dfw-emails__cols${wide ? ' is-wide' : ''}`}>
        <section className="dfw-group" aria-labelledby="dfw-e-title">
          <h3 className="dfw-group__title" id="dfw-e-title">{current.title}</h3>
          <p className="dfw-hint dfw-hint--flush">{current.about}</p>

          {kind === 'submission' && (
            <p className="dfw-note">Goes to the answer of “{emailLabel || 'the email question'}”.</p>
          )}
          <div className="dfw-row">
            <div className="dfw-field">
              <label className="dfw-label" htmlFor={`dfw-e-${kind}-to`}>{current.toLabel}</label>
              <Input id={`dfw-e-${kind}-to`} type="email" value={v('to')} placeholder={current.toPlaceholder} onChange={(e) => set(`${kind}_to`, e.target.value)} status={errors[`${kind}_to`] ? 'error' : undefined} />
              {errors[`${kind}_to`] && <p className="dfw-error" role="alert">{errors[`${kind}_to`]}</p>}
            </div>
            <div className="dfw-field">
              <label className="dfw-label" htmlFor={`dfw-e-${kind}-cc`}>Copy to <em>optional</em></label>
              <Input id={`dfw-e-${kind}-cc`} value={v('cc')} placeholder="one@example.com, two@example.com" onChange={(e) => set(`${kind}_cc`, e.target.value)} status={errors[`${kind}_cc`] ? 'error' : undefined} />
              {errors[`${kind}_cc`] && <p className="dfw-error" role="alert">{errors[`${kind}_cc`]}</p>}
            </div>
          </div>
          <div className="dfw-field">
            <label className="dfw-label" htmlFor={`dfw-e-${kind}-subject`}>Subject</label>
            <Input id={`dfw-e-${kind}-subject`} value={v('subject')} onChange={(e) => set(`${kind}_subject`, e.target.value)} placeholder="e.g. We received your application" />
          </div>
          <div className="dfw-field">
            <label className="dfw-label" htmlFor={`dfw-e-${kind}-body`}>Message</label>
            <TextArea id={`dfw-e-${kind}-body`} rows={8} value={v('body')} onChange={(e) => set(`${kind}_body`, e.target.value)} placeholder="Write the message. New lines are kept." />
            <p className="dfw-hint">New lines are kept. HTML such as &lt;b&gt; or &lt;a href=…&gt; also works.</p>
          </div>
          <div className="dfw-field">
            <label className="dfw-label" htmlFor={`dfw-e-${kind}-footer`}>Sign-off</label>
            <TextArea id={`dfw-e-${kind}-footer`} rows={3} value={v('footer')} onChange={(e) => set(`${kind}_footer`, e.target.value)} placeholder={'Kind regards,\nFestivals Malta'} />
          </div>
        </section>

        <aside className="dfw-mailview" aria-label="Message preview">
          <header className="dfw-mailview__head">
            <span className="dfw-tile" aria-hidden="true"><MailTwoTone twoToneColor="#2748d9" /></span>
            <span>
              <strong>{v('subject') || defaults.subject}</strong>
              <small>{kind === 'submission' ? `To the applicant${v('cc') ? `, copy to ${v('cc')}` : ''}` : `To ${v('to') || 'no one yet'}${v('cc') ? `, copy to ${v('cc')}` : ''}`}</small>
            </span>
          </header>
          <iframe className="dfw-mailview__body" title="Message text" sandbox="" srcDoc={previewDoc} />
          <p className="dfw-hint">
            {kind === 'submission'
              ? 'As the applicant receives it. Empty texts use the standard wording shown here.'
              : `As it is sent, with the application attached as a PDF${kind === 'external' ? ' and download links for uploaded files under the sign-off' : ''}. Empty texts use the standard wording shown here.`}
          </p>
        </aside>
      </div>

      <div className="dfw-savebar">
        <span className="dfw-savebar__state">{dirty ? 'You have changes that are not saved.' : 'All changes saved.'}</span>
        <span className="dfw-grow" />
        {dirty && config && <Button onClick={() => { setValues(valuesFrom(config)); setErrors({}); }}>Undo changes</Button>}
        <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>Save emails</Button>
      </div>
    </div>
  );
};

export default EmailsEditor;
